/**
 * Legal Documents Configuration
 *
 * IMPORTANT: Keep versions in sync with backend/src/config/legalDocuments.ts
 *
 * To publish a new version:
 * 1. Add the new MD file to compliance-context/legal/{type}/v{version}.md
 * 2. Optionally add a "what's new" file: v{version}-changes.md (same folder)
 * 3. Update currentVersion below
 * 4. Build — paths are derived automatically from the version number
 */

export type DocumentType = 'tos' | 'privacy' | 'ai_notice' | 'cookies' | 'profiling' | 'licenses';
export type MandatoryDocumentType = 'tos' | 'privacy' | 'ai_notice';

export interface LegalDocumentConfig {
  currentVersion: string;
  requiresAcceptance: boolean;
}

// Map config keys to URL folder names (must match copy-legal-docs.js output)
const DOCUMENT_FOLDERS: Record<string, string> = {
  tos: 'tos',
  privacy: 'privacy',
  ai_notice: 'ai-notice',
  cookies: 'cookies',
  licenses: 'licenses',
};

// Document display names in Slovak
export const DOCUMENT_NAMES: Record<string, string> = {
  tos: 'Podmienky používania',
  privacy: 'Zásady ochrany osobných údajov',
  ai_notice: 'Informácie o používaní umelej inteligencie',
  cookies: 'Zásady cookies',
  profiling: 'Profilovací súhlas',
  licenses: 'Licenčné podmienky',
};

// Profiling consent version (tracked separately from documents)
export const PROFILING_CONSENT_VERSION = '1.0.0';

export const MANDATORY_DOCUMENT_TYPES: MandatoryDocumentType[] = ['tos', 'privacy', 'ai_notice'];

/**
 * Legal Documents Registry
 *
 * Only update currentVersion when publishing new versions.
 * Paths are derived automatically.
 */
export const LEGAL_DOCUMENTS: Record<string, LegalDocumentConfig> = {
  tos: {
    currentVersion: '1.0.2',
    requiresAcceptance: true,
  },
  privacy: {
    currentVersion: '1.0.2',
    requiresAcceptance: true,
  },
  ai_notice: {
    currentVersion: '1.0.1',
    requiresAcceptance: true,
  },
  cookies: {
    currentVersion: '1.0.0',
    requiresAcceptance: false,
  },
  licenses: {
    currentVersion: '1.0.0',
    requiresAcceptance: false,
  },
};

/**
 * Get the URL path for a document version
 * Convention: /legal/{folder}/v{version}.md
 */
export function getDocumentPath(documentType: string, version?: string): string {
  const folder = DOCUMENT_FOLDERS[documentType];
  if (!folder) throw new Error(`Unknown document type: ${documentType}`);
  const v = version || LEGAL_DOCUMENTS[documentType].currentVersion;
  return `/legal/${folder}/v${v}.md`;
}

/**
 * Get the URL path for a "what's new" changes file
 * Convention: /legal/{folder}/v{version}-changes.md
 * The file may or may not exist — caller should handle 404
 */
export function getChangesPath(documentType: string, version?: string): string {
  const folder = DOCUMENT_FOLDERS[documentType];
  if (!folder) throw new Error(`Unknown document type: ${documentType}`);
  const v = version || LEGAL_DOCUMENTS[documentType].currentVersion;
  return `/legal/${folder}/v${v}-changes.md`;
}

/**
 * Compare two semantic version strings
 * @returns 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 */
export function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < 3; i++) {
    if (parts1[i] > parts2[i]) return 1;
    if (parts1[i] < parts2[i]) return -1;
  }
  return 0;
}

/**
 * Check if user needs to accept updated legal documents
 */
export function needsAcceptance(
  documentType: MandatoryDocumentType,
  userAcceptedVersion: string | null
): boolean {
  const config = LEGAL_DOCUMENTS[documentType];
  if (!config.requiresAcceptance) return false;
  if (!userAcceptedVersion) return true;
  return compareVersions(config.currentVersion, userAcceptedVersion) > 0;
}
